/**
 * EliaAdvisorFAB — Le bouton flottant d'ELIA, présent sur toutes les pages.
 *
 * ELIA n'est pas un chatbot. C'est une conseillère financière qui connaît
 * en permanence l'intégralité du dossier de l'utilisateur.
 *
 * Depuis le bouton flottant, un drawer s'ouvre avec :
 *   • Suggestions de questions pré-formulées (contextuelles)
 *   • Chat streaming en direct (SSE)
 *   • Historique de la session
 *
 * Le contexte est automatiquement injecté côté backend — le frontend passe
 * juste projectId (si présent dans l'URL /projects/:projectId/*).
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, X, MessageCircle } from "lucide-react";
import api from "@/lib/api";


function useCurrentProjectId() {
  const loc = useLocation();
  // Détecte /projects/:projectId dans l'URL
  const m = loc.pathname.match(/\/projects\/([^/]+)/);
  return m ? m[1] : null;
}


function StreamingMessage({ content }) {
  return (
    <div className="whitespace-pre-wrap text-sm leading-relaxed text-ink">
      {content}
      <span className="inline-block w-1.5 h-3.5 bg-primary ml-0.5 align-middle animate-pulse" />
    </div>
  );
}


function AssistantBubble({ content }) {
  return (
    <div className="flex gap-3 items-start" data-testid="elia-bubble-assistant">
      <div className="shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
        <Sparkles className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0 max-w-full">
        <div className="whitespace-pre-wrap text-sm leading-relaxed text-ink">{content}</div>
      </div>
    </div>
  );
}


function UserBubble({ content }) {
  return (
    <div className="flex justify-end" data-testid="elia-bubble-user">
      <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap">
        {content}
      </div>
    </div>
  );
}


export default function EliaAdvisorFAB() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content}
  const [streaming, setStreaming] = useState("");
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const projectId = useCurrentProjectId();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const url = projectId ? `/elia/suggestions?project_id=${projectId}` : "/elia/suggestions";
    api.get(url).then((r) => setSuggestions(r.data.items || [])).catch(() => {});
  }, [open, projectId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (text) => {
    const clean = (text || "").trim();
    if (!clean || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: clean }]);
    setStreaming("");
    setSending(true);
    try {
      const token = localStorage.getItem("token") || "";
      const backend = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backend}/api/elia/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: clean, project_id: projectId, session_id: sessionId }),
      });
      if (!resp.body) throw new Error("no stream");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              acc += obj.delta;
              setStreaming(acc);
            } else if (obj.done) {
              if (obj.session_id) setSessionId(obj.session_id);
              setMessages((prev) => [...prev, { role: "assistant", content: acc }]);
              setStreaming("");
            } else if (obj.error) {
              setMessages((prev) => [...prev, { role: "assistant", content: `Désolée, un incident technique : ${obj.error}` }]);
            }
          } catch {
            // ignore malformed chunk
          }
        }
      }
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", content: "Je n'ai pas pu répondre — retente dans un instant." }]);
    } finally {
      setSending(false);
      setStreaming("");
    }
  };

  return (
    <>
      {/* Floating action button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.5, type: "spring", stiffness: 260 }}
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-6 z-40 group flex items-center gap-2 pl-3.5 pr-5 h-14 rounded-full bg-primary text-primary-foreground shadow-premium hover:shadow-glow hover:-translate-y-0.5 transition-all"
        data-testid="elia-fab"
        aria-label="Parler à ELIA"
      >
        <span className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center">
          <Sparkles className="w-4 h-4" />
        </span>
        <span className="font-medium">Parler à ELIA</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-end sm:items-center sm:justify-end"
            onClick={() => setOpen(false)}
            data-testid="elia-modal"
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 24 }}
              className="bg-background w-full sm:w-[420px] h-[88vh] sm:h-[92vh] sm:mr-6 rounded-t-3xl sm:rounded-3xl shadow-premium flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="px-5 py-4 border-b border-border/60 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
                    <Sparkles className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-ink">ELIA</div>
                    <div className="text-[11px] text-muted-foreground">
                      Ta conseillère financière · en ligne
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-primary"
                  data-testid="elia-close"
                  aria-label="Fermer"
                >
                  <X className="w-4.5 h-4.5" />
                </button>
              </div>

              {/* Messages */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
                data-testid="elia-messages"
              >
                {messages.length === 0 && streaming === "" && (
                  <div className="space-y-4">
                    <div className="p-4 rounded-2xl bg-primary-subtle/50 border border-primary/20">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                          <Sparkles className="w-4 h-4" />
                        </div>
                        <div className="text-sm text-ink leading-relaxed">
                          Bonjour, je suis <strong>ELIA</strong>. Je connais déjà ton profil, ton pays et l'état de ton dossier. Pose-moi n'importe quelle question ou choisis un sujet ci-dessous.
                        </div>
                      </div>
                    </div>
                    {suggestions.length > 0 && (
                      <div>
                        <div className="elia-overline mb-2">Suggestions</div>
                        <div className="flex flex-col gap-2">
                          {suggestions.map((s, i) => (
                            <button
                              key={i}
                              onClick={() => send(s)}
                              className="text-left p-3 rounded-xl border border-border hover:border-primary hover:bg-primary-subtle/40 transition-all text-sm text-ink active:scale-[0.98]"
                              data-testid={`elia-suggestion-${i}`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {messages.map((m, i) => (
                  m.role === "user"
                    ? <UserBubble key={i} content={m.content} />
                    : <AssistantBubble key={i} content={m.content} />
                ))}
                {streaming && (
                  <div className="flex gap-3 items-start" data-testid="elia-streaming">
                    <div className="shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0"><StreamingMessage content={streaming} /></div>
                  </div>
                )}
              </div>

              {/* Composer */}
              <div className="px-4 py-3 border-t border-border/60 bg-background">
                <form
                  onSubmit={(e) => { e.preventDefault(); send(input); }}
                  className="flex items-center gap-2"
                >
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Pose ta question à ELIA…"
                    disabled={sending}
                    className="flex-1 h-11 px-4 rounded-full border border-border bg-background text-sm outline-none focus:border-primary"
                    data-testid="elia-input"
                  />
                  <button
                    type="submit"
                    disabled={sending || !input.trim()}
                    className="w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 hover:shadow-subtle"
                    data-testid="elia-send"
                    aria-label="Envoyer"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
                <p className="mt-2 text-[10px] text-muted-foreground text-center">
                  ELIA connaît ton dossier en temps réel. Réponses contextualisées à partir de tes données réelles.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
